import * as Tone from 'tone';
import SamJs from 'sam-js';
import Utils from './utils';

export default class AudioManager {

	constructor(args) {
		this.system = args.system;
		this.music = args.music;
		this.sounds = args.sounds;
		this.impulses = args.impulses;
		this.musicPlayers = {};
		this.samplePlayers = {};
		this.speechPlayer = null;
		this.pools = {};
		this.convolverEnabled = false;
	}

	init(callback) {
		Tone.Transport.bpm.value = 120;
		Tone.Transport.PPQ = 60;

		this.master = new Tone.Gain(1).toDestination();
		this.out = new Tone.Gain(1).connect(this.master);

		Tone.start();
		Tone.context.lookAhead = 0; // reduce latency
		this.resumeAll();

		this.preload(callback);

		if(this.system.debug)	window.Tone = Tone;
	}

	// real cheap and ugly preloading
	preload(callback) {
		let soundsToLoad = Object.entries(this.sounds).length;
		if(soundsToLoad == 0) {
			// nothing to preload!
			callback();
			return;
		}
		for(let s in this.sounds) {
			this.playSample(s, { volume:-3, onload:() => {
				soundsToLoad--;
				if(soundsToLoad == 0) {
					log('all samples loaded', '#999900');
					callback();
				}
			}});
		}
	}
	
	enableConvolver(name) {
		if(!this.convolverEnabled) {
			this.out.disconnect(this.master);
			this.convolver = new Tone.Convolver(this.impulses[name]).connect(this.master);
			this.out.connect(this.convolver);
			this.convolverEnabled = true;
		}
	}

	bypassConvolver() {
		if(this.convolverEnabled) {
			this.convolver.disconnect(this.master);
			this.out.disconnect(this.convolver);
			this.out.connect(this.master);
			this.convolverEnabled = false;
		}
	}

	floatToDecibel(float, range=48) {
		// convert volume between 0—1 to a dB value, default range of -48dB
		// TODO: non linear conversion...
		return (-1+float)*range;
	}

	/**
	 * @param {String} name music filename (sans extension) from /audio/music to be played
	 * @param {Number} volume float number, where 1 is original volume, 0 is silence, etc.
	 * @param {Boolean} loop
	 */
	playMusic(name, volume=1, loop=true) {
		this.stopMusic(); // stop everything else
		if(this.musicPlayers[name] == undefined) {
			// music not played before, make a new player
			log('loading music: '+name, '#aaaa00');
			let m = new Tone.Player({
				url:this.music[name],
				fadeOut: 0.5,
				loop: loop,
				volume: this.floatToDecibel(volume),
				onload: () => {
					m.sync();
					m.start();
				}
			}).connect(this.out);
			m._state.memory = 8;
			this.musicPlayers[name] = m;
		} else {
			// music played before
			let m = this.musicPlayers[name];
			if(m.loaded) {
				m.sync();
				m.volume.value = this.floatToDecibel(volume);
				m.start();
			} else {
				// still loading... ignore additional play request
			}
		}
	}

	stopMusic() {
		for(let key in this.musicPlayers) {
			let m = this.musicPlayers[key];
			m.stop();
		}
	}

	/**
	 * @param {String} name sample filename (sans extension) from /audio/sounds to be played
	 * @param {Object} options optional options...
	 * @param {Number} options.volume float number, where 1 is original volume, 0 is silence, etc.
	 * @param {Number} options.pitch float number where 1 is original pitch, 0.5 is half speed, etc.
	 * @param {Number} options.pan float number where 0 is stereo centre, -1 is full left, etc.
	 * @param {Number} options.onload callback when the sample loads (if not loaded already)
	 */
	playSample(name, options={}) {
		let o = {};
		o.volume = options.volume == undefined ? 1 : options.volume;
		o.pitch = options.pitch || 1;
		o.pan = options.pan || 0;
		o.volume = this.floatToDecibel(o.volume);
		if(this.samplePlayers[name] == undefined) {
			// sample not played before, make a new player
			log('loading sample: '+name, '#009966');
			let p = new Tone.Panner({ pan: o.pan }).connect(this.out);
			let s = new Tone.Player({
				url:this.sounds[name],
				autostart:true,
				playbackRate:o.pitch,
				volume:o.volume,
				onload:options.onload,
			}).connect(p);
			s._PANNER = p;
			s._state.memory = 0; // stop tone.js' timeline faff, just play the damn sample
			this.samplePlayers[name] = s;
		} else {
			// sample played before
			let s = this.samplePlayers[name];
			if(s.loaded) {
				s._PANNER.pan.value = o.pan;
				s.volume.value = o.volume;
				s.playbackRate = o.pitch;
				s.start();
			} else {
				// still loading... ignore additional play request
			}
		}
	}

	/**
	 * @param {String} name name of the multi sample pool, to be referred to when playing from this pool
	 * @param {Array} sounds array of sample filenames (sans extensions) from /audio/sounds to be included in this pool
	 * @param {String} mode sample ordering mode: 'order' plays samples in the defined order, 'random' chooses a random sample every time
	 */
	defineMulti(name, sounds, mode='random') {
		this.pools[name] = {
			sounds:sounds,
			index:-1,
			mode:mode,
		};
	}

	/**
	 * @param {String} name multi sample pool name to choose a sound from (as defined by defineMulti)
	 * @param {Object} options optional options...
	 * @param {Number} options.volume float number, where 1 is original volume, 0 is silence, etc.
	 * @param {Number} options.pitch float number where 1 is original pitch, 0.5 is half speed, etc.
	 * @param {Number} options.pan float number where 0 is stereo centre, -1 is full left, etc.
	 */
	playMulti(name, options={}) {
		let pool = this.pools[name];
		let i = 0;
		switch(pool.mode) {
			case 'order' :
				pool.index++;
				pool.index %= pool.sounds.length;
				break;
			case 'random' :
				pool.index = Utils.randomInt(0, pool.sounds.length-1);
				break;
		}
		this.playSample(pool.sounds[pool.index], options);
	}

	/**
	 * @param {String} text words to be spoken
	 * @param {Object} options optional options...
	 * @param {Number} options.speed integer between 1 and 255, default 64
	 * @param {Number} options.pitch integer between 0 and 255, default 80
	 * @param {Number} options.mouth timbre of vowel components, integer between 0 and 255, default 128
	 * @param {Number} options.throat timbre of breath components, integer between 0 and 255, default 128
	 * @param {Boolean} options.singmode pitch will be static, default false
	 * @param {Boolean} options.phoenetic text will be parsed using the phoenetic spelling system, default false, see: https://github.com/discordier/sam/blob/master/docs/manual.md#ch1.0
	 * @param {Number} options.repitch rendered sample will be played back at this rate, default 0.5, minimum 0.1
	 */
	speak(text, options={}) {
		let o = {};
		o.speed = options.speed || 64;
		o.pitch = options.pitch || 80;
		o.throat = options.throat || 128;
		o.mouth = options.mouth || 128;
		o.singmode = options.singmode || false;
		let sam = new SamJs(o);
		let array = sam.buf32(text, options.phoenetic || false);
		let buffer = Tone.ToneAudioBuffer.fromArray(array);
		if(this.speechPlayer != null) {
			this.speechPlayer.stop().dispose();
			this.speechPlayer = null;
		}
		let p = new Tone.Player({ url:buffer, playbackRate:options.repitch || 0.5 }).connect(this.out);
		p.sync().start();
		p._state.memory = 0;
		this.speechPlayer = p;
		log('speaking: '+text.substring(0, 16)+'...', '#0088aa');
	}

	suspendAll() {
		// app unfocused
		Tone.Transport.pause();
		Tone.Destination.mute = true;
	}
	
	resumeAll() {
		// app focused
		Tone.Transport.start();
		Tone.Destination.mute = false;
	}

}